import React, { useMemo } from "react";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import { Button } from "../../ui/button";
import { CopyIcon } from "lucide-react";
import { ReactComponent as AiStars } from "../../../assets/svg/ai-creation.svg";
import PaginatedTable, { PaginatedTableColumn } from "../../ui/paginated-table";
import { RequestViewButton } from "../../shared/button/Buttons";
import moment from "moment";
import { ChatCompletionRequest } from "../../../model_gen/chat";

interface DatasetRequestsTableProps {
  requests: ChatCompletionRequest[];
  requestsTotal: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  
  // Handlers
  onPageChange: (page: number) => void;
  onRowClick: (request: ChatCompletionRequest) => void;
  
  // API key
  apiKey: ApiKeyOrGetter;
  
  // Optional customization
  emptyTitle?: string;
  emptyDescription?: string;
}

const DatasetRequestsTable: React.FC<DatasetRequestsTableProps> = ({
  requests,
  requestsTotal,
  currentPage,
  pageSize,
  loading,
  onPageChange,
  onRowClick,
  apiKey,
  emptyTitle = "No requests found",
  emptyDescription = "Add requests to this dataset to get started."
}) => {
  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Define columns for the table
  const columns: PaginatedTableColumn<ChatCompletionRequest>[] = useMemo(() => [
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[15%]",
      render: (request) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(request.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(request.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "metadata",
      header: (
        <div className="flex items-center gap-1">
          Tags
          <AiStars className="w-3 h-3 text-muted-foreground" />
        </div>
      ),
      width: "w-[50%]",
      render: (request) => (
        <div className="flex flex-wrap gap-1">
          {(() => {
            try {
              const metaData = JSON.parse(
                (request.metadata?._maitai_request_meta as string) || "{}"
              );
              const tags = metaData.tags || [];
              return tags.length > 0 ? (
                tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              );
            } catch (e) {
              return <span className="text-sm text-muted-foreground">—</span>;
            }
          })()}
        </div>
      )
    },
    {
      key: "request_id",
      header: "Request ID",
      width: "w-[20%]",
      render: (request) => (
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
            {request.request_id && request.request_id.length > 12
              ? `${request.request_id.substring(0, 6)}...${request.request_id.substring(
                  request.request_id.length - 6
                )}`
              : request.request_id}
          </code>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(request.request_id || "");
            }}
          >
            <CopyIcon className="w-3 h-3" />
          </Button>
        </div>
      )
    },
    {
      key: "request_id" as keyof ChatCompletionRequest,
      header: "Actions",
      width: "w-[15%]",
      render: (request) => (
        <RequestViewButton
          apiKey={apiKey}
          request={request}
        >
          Details
        </RequestViewButton>
      )
    }
  ], [apiKey]);

  return (
    <PaginatedTable
      data={requests}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalItems={requestsTotal}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      onRowClick={onRowClick}
      rowClassName="cursor-pointer hover:bg-accent/20 transition-colors"
      serverSidePagination={true}
    />
  );
};

export default DatasetRequestsTable; 